import { useQueryClient } from 'react-query'
import { AxiosError } from 'axios'
import format from 'date-fns/format'

import {
  AvailabilityRange,
  getReporterWeeklyAvailabilityQueryKey,
  reporterWeeklyAvailability,
  useReporterWeeklyAvailability,
  useSetReporterWeeklyAvailability,
  WeeklyAvailability,
} from '@/apiClient'

export const WEEK_START_FORMAT = 'yyyy-MM-dd'

export const formatWeekStart = (weekStart: Date): string =>
  format(weekStart, WEEK_START_FORMAT)

export const useWeeklyAvailabilityQuery = (
  reporterId: string,
  weekStart: Date,
  options?: { enabled?: boolean },
) =>
  useReporterWeeklyAvailability(
    reporterId,
    { week_start: formatWeekStart(weekStart) },
    { query: { enabled: options?.enabled ?? true, staleTime: 0 } },
  )

export const useSetWeeklyAvailabilityMutation = () => {
  const queryClient = useQueryClient()

  return useSetReporterWeeklyAvailability({
    mutation: {
      onSuccess: (response, { id, data }) => {
        queryClient.setQueryData(
          getReporterWeeklyAvailabilityQueryKey(id, {
            week_start: data.week_start,
          }),
          response,
        )
      },
    },
  })
}

export const useFetchWeeklyAvailability = () => {
  const queryClient = useQueryClient()

  return (reporterId: string, weekStart: Date) =>
    queryClient.fetchQuery(
      getReporterWeeklyAvailabilityQueryKey(reporterId, {
        week_start: formatWeekStart(weekStart),
      }),
      () =>
        reporterWeeklyAvailability(reporterId, {
          week_start: formatWeekStart(weekStart),
        }),
    )
}

const isValidationErrorList = (detail: unknown): detail is { msg: string }[] =>
  Array.isArray(detail) && detail.every(item => typeof item?.msg === 'string')

export const getWeeklyAvailabilityErrorMessage = (
  error: AxiosError<{ detail?: unknown }> | null | undefined,
  fallback: string,
): string => {
  const detail = error?.response?.data?.detail

  if (typeof detail === 'string' && detail) {
    return detail
  }

  if (isValidationErrorList(detail) && detail.length > 0) {
    return detail.map(item => item.msg).join(' ')
  }

  return fallback
}

export type { AvailabilityRange, WeeklyAvailability }
